// Types and pure helpers for the trade accrual bank page (mirror server `accrual_bank`).

export interface accrual_cell {
  planned: number
  swept: number
  actual: number
}

export interface bucket_row {
  bucket_id: string | null
  bucket_name: string
  rollup: string
  months: accrual_cell[]
  total: accrual_cell
}

export interface account_row {
  account_id: string | null
  account_number: string | null
  account_label: string
  months: accrual_cell[]
  total: accrual_cell
  buckets: bucket_row[]
}

export interface accrual_line_item {
  split_id: string
  deduction_id: string
  invoice_number: string
  source: string
  retailer_name: string | null
  account_id: string | null
  bucket_id: string | null
  month: string
  amount: number
  validated: boolean
}

export interface accrual_bucket {
  id: string
  org_id: string
  name: string
  rollup: string
  source: string
  match_patterns: string[]
  is_default: boolean
  sort_order: number
}

export interface accrual_entry {
  id: string
  bucket_id: string
  account_id: string
  month: string
  kind: "plan" | "sweep"
  amount: number
  note: string | null
}

export interface bank_account {
  id: string
  account_number: string | null
  name: string
  label: string
  is_trade: boolean
  hidden: boolean
}

export interface accrual_bank_report {
  year: number
  rows: account_row[]
  grand_total: account_row
  line_items: accrual_line_item[]
  unbucketed_count: number
  awaiting_validation_count: number
  awaiting_validation_amount: number
  buckets: accrual_bucket[]
  entries: accrual_entry[]
  accounts: bank_account[]
}

export type bank_view = "plan" | "actual" | "remaining"

export const MONTH_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]

/** The value a cell shows under each view. Math in cents so 0.1 + 0.2 reads 0.30. */
export function cell_value(cell: accrual_cell, view: bank_view): number {
  const planned = Math.round(cell.planned * 100)
  const swept = Math.round(cell.swept * 100)
  const actual = Math.round(cell.actual * 100)
  switch (view) {
    case "plan":
      return (planned + swept) / 100
    case "actual":
      return actual / 100
    case "remaining":
      return (planned + swept - actual) / 100
  }
}

/** Line items behind one actual cell: account × (bucket, or any) × month index (0-11). */
export function line_items_for(
  items: accrual_line_item[],
  account_id: string | null,
  bucket_id: string | null | undefined,
  month_index: number
): accrual_line_item[] {
  return items.filter((item) => {
    if (item.account_id !== account_id) return false
    if (bucket_id !== undefined && item.bucket_id !== bucket_id) return false
    return month_index_of(item.month) === month_index
  })
}

/** 0-11 from an ISO date (`2026-04-01`). */
export function month_index_of(iso_date: string): number {
  return Number(iso_date.slice(5, 7)) - 1
}

/** The ISO first-of-month for a year and 0-11 index. */
export function month_iso(year: number, month_index: number): string {
  return `${year}-${String(month_index + 1).padStart(2, "0")}-01`
}

/** Sweeps recorded for a bucket × account × month, newest last. */
export function sweeps_for(entries: accrual_entry[], bucket_id: string, account_id: string, month_index: number): accrual_entry[] {
  return entries.filter(
    (e) => e.kind === "sweep" && e.bucket_id === bucket_id && e.account_id === account_id && month_index_of(e.month) === month_index
  )
}

/** Comma- or newline-separated patterns typed into the buckets form → clean list. */
export function parse_patterns(text: string): string[] {
  return text
    .split(/[,\n]/)
    .map((p) => p.trim())
    .filter((p) => p.length > 0)
}

/** A bucket × GL row the user added on the page before any amount was saved. */
export interface added_row {
  account_id: string
  bucket_id: string
}

export function empty_cell(): accrual_cell {
  return { planned: 0, swept: 0, actual: 0 }
}

function empty_bucket_row(bucket: accrual_bucket): bucket_row {
  return { bucket_id: bucket.id, bucket_name: bucket.name, rollup: bucket.rollup, months: Array.from({ length: 12 }, empty_cell), total: empty_cell() }
}

/**
 * Report rows plus the rows the user added on the page: a bucket under an existing GL, or a new
 * GL with its first bucket. Rows the report already has are left alone, so a row stops being
 * "added" the moment its first amount is saved and comes back from the server.
 */
export function merge_added_rows(rows: account_row[], added: added_row[], accounts: bank_account[], buckets: accrual_bucket[]): account_row[] {
  const out: account_row[] = rows.map((r) => ({ ...r, buckets: [...r.buckets] }))
  for (const a of added) {
    const bucket = buckets.find((b) => b.id === a.bucket_id)
    const account = accounts.find((acc) => acc.id === a.account_id)
    if (!bucket || !account) continue
    let row = out.find((r) => r.account_id === a.account_id)
    if (!row) {
      row = {
        account_id: account.id,
        account_number: account.account_number,
        account_label: account.label,
        months: Array.from({ length: 12 }, empty_cell),
        total: empty_cell(),
        buckets: [],
      }
      out.push(row)
    }
    if (!row.buckets.some((b) => b.bucket_id === bucket.id)) {
      row.buckets.push(empty_bucket_row(bucket))
    }
  }
  return out
}

/** Parse what was typed into an accrued cell: "1,200.5", "$300", "(50)" or blank (= 0). */
export function parse_amount_input(text: string): number | null {
  const cleaned = text.replace(/[$,\s]/g, "")
  if (cleaned === "" || cleaned === "-") return 0
  const paren = cleaned.match(/^\((.*)\)$/)
  const body = paren ? paren[1] : cleaned
  const value = Number(body)
  if (!Number.isFinite(value)) return null
  const cents = Math.round(value * 100)
  return (paren ? -cents : cents) / 100
}
